import type {
  SystemActionEvent,
  TaskPulseEvent,
  TaskPulseEntryKind,
  TaskPulseStage,
  TaskPulseEntry,
  TaskThreadAttachment,
  TaskThreadAttachmentDto,
  TaskThreadEvent,
  TaskThreadEventDto,
  TaskThreadPulseEntryDto,
  UserCommentEvent,
  WorkspaceTaskThreadResponse,
  TaskThreadPulseEntryWorkspaceTargetDto,
  TaskPulseEntryWorkspaceTarget,
} from "@/types/task-thread";

export type ThreadNormalizerContext = {
  userName?: string | null;
  userPictureUrl?: string | null;
  namespaceIcon?: string | null;
};

const SUPPORTED_PULSE_STAGES: TaskPulseStage[] = [
  "reading",
  "orchestrating",
  "executing",
  "error",
  "finished",
];

const SUPPORTED_PULSE_KINDS: TaskPulseEntryKind[] = [
  "start",
  "reading",
  "planning",
  "preparing",
  "executing",
  "finalizing",
  "completed",
  "failed",
  "canceled",
  "paused",
  "syncing",
  "publishing",
  "commit",
  "action",
  "step_pending",
  "step_active",
  "step_completed",
  "querying_model",
  "reading_file",
  "writing_file",
  "editing_file",
  "browsing_files",
  "deleting_file",
  "creating_directory",
  "moving_file",
  "writing_skill",
  "using_skill",
  "deleting_skill",
  "listing_skills",
  "fetching_url",
  "googling",
  "creating_cron",
  "updating_cron",
  "listing_cron",
  "removing_cron",
  "completing_cron",
];

export function normalizeTaskThreadEvents(
  response: WorkspaceTaskThreadResponse | null | undefined,
  fallbackTaskId?: string,
  context?: ThreadNormalizerContext,
) {
  if (!response?.events?.length) {
    return [] as TaskThreadEvent[];
  }

  const resolvedTaskId = resolveTaskIdentifier(response.taskId, fallbackTaskId);

  return response.events
    .map((event) => normalizeTaskThreadEvent(event, resolvedTaskId, context))
    .filter((event): event is TaskThreadEvent => Boolean(event));
}

export function normalizeTaskThreadEvent(
  event: TaskThreadEventDto,
  fallbackTaskId?: string,
  context?: ThreadNormalizerContext,
): TaskThreadEvent | null {
  if (!event?.type || !event.id) {
    return null;
  }

  const timestamp = toUnixMs(event.timestamp);
  const type = event.type.toLowerCase();

  if (type === "task_pulse") {
    const entries = normalizePulseEntries(event.entries);
    if (!entries.length) {
      return null;
    }

    const taskId = resolveTaskIdentifier(fallbackTaskId);

    return {
      type: "task_pulse",
      id: event.id,
      taskId,
      taskLabel: event.label ?? event.summary ?? `Task ${taskId} · Activity`,
      entries,
      collapsedByDefault: false,
    } satisfies TaskPulseEvent;
  }

  if (type === "state_change" || type === "property_change") {
    return {
      type: "state_change",
      id: event.id,
      timestamp,
      author: event.author ?? undefined,
      label: event.label ?? event.summary ?? "State change",
      detail: event.detail ?? undefined,
    };
  }

  if (type === "system_action") {
    const actionLabel = event.content?.trim();
    return {
      type: "system_action",
      id: event.id,
      timestamp,
      summary: event.summary ?? event.label ?? "System action",
      actions: actionLabel
        ? [
            {
              id: "acknowledge",
              label: actionLabel,
            },
          ]
        : undefined,
      isHistorical: event.isHistorical ?? undefined,
      status: event.isHistorical ? "completed" : undefined,
    } satisfies SystemActionEvent;
  }

  // Default to user comment semantics for any other record.
  const role = normalizeAuthorRole(event.authorRole);
  const isSystem = role === "system";
  const isOperator = role === "operator";

  let author = event.author ?? (isSystem ? "Scirix" : "Unknown");
  let initials = deriveInitials(author);
  let avatarImageUrl: string | undefined;

  if (isSystem) {
    author = "Scirix";
    const icon = context?.namespaceIcon;
    if (icon && (icon.startsWith('/') || icon.startsWith('http'))) {
      avatarImageUrl = icon;
      initials = "S";
    } else {
      initials = icon || "S";
    }
  } else if (isOperator && context?.userName) {
    author = context.userName;
    initials = deriveInitials(context.userName);
    avatarImageUrl = context.userPictureUrl ?? undefined;
  }

  return {
    type: "user_comment",
    id: event.id,
    timestamp,
    author,
    authorRole: role,
    avatarInitials: initials,
    avatarImageUrl,
    content: event.content ?? event.summary ?? "(No content provided)",
    deliveryStatus: "delivered",
    deliveryTimestamp: formatThreadTimestamp(event.timestamp),
    readReceipts: [],
    attachments: normalizeAttachments(event.attachments),
  } satisfies UserCommentEvent;
}

function normalizeAttachments(
  attachments: TaskThreadAttachmentDto[] | null | undefined,
): TaskThreadAttachment[] | undefined {
  if (!attachments?.length) {
    return undefined;
  }

  const normalized: TaskThreadAttachment[] = [];

  for (const attachment of attachments) {
    if (!attachment || typeof attachment.id !== "number") {
      continue;
    }

    const name = attachment.name?.trim();
    if (!name) {
      continue;
    }

    normalized.push({
      id: attachment.id.toString(),
      name,
      size: typeof attachment.size === "number" ? attachment.size : 0,
      downloadUrl: attachment.downloadUrl ?? "",
      contentType: attachment.contentType ?? undefined,
      isImage: Boolean(attachment.isImage),
    });
  }

  return normalized.length ? normalized : undefined;
}

function normalizePulseEntries(
  rawEntries: TaskThreadPulseEntryDto[] | null | undefined,
): TaskPulseEntry[] {
  if (!rawEntries?.length) {
    return [];
  }

  // Sort by raw ISO timestamp for second/millisecond precision, but use the
  // original array index as a tiebreaker so entries with identical timestamps
  // keep the backend's order.  Without this, same-timestamp entries of
  // different kinds can be reordered, causing the consecutive-grouping logic
  // in TaskPulseCard to merge entries that had an intervening event.
  const withRaw: Array<{ raw: string; order: number; entry: TaskPulseEntry }> = [];

  for (const entry of rawEntries) {
    if (!entry || !entry.id || !entry.summary) {
      continue;
    }

    const stage = normalizePulseStage(entry.stage);
    if (!stage) {
      continue;
    }

    withRaw.push({
      raw: entry.timestamp ?? "",
      order: withRaw.length,
      entry: {
        id: entry.id,
        timestamp: toUnixMs(entry.timestamp),
        stage,
        kind: normalizePulseKind(entry.kind),
        summary: entry.summary,
        detail: entry.detail ?? undefined,
        isActive: entry.isActive ?? undefined,
        workspaceTargets: normalizeWorkspaceTargets(entry.workspaceTargets),
      },
    });
  }

  withRaw.sort((a, b) => a.raw.localeCompare(b.raw) || a.order - b.order);

  return withRaw.map((item) => item.entry);
}

function normalizePulseStage(stage: string | null | undefined): TaskPulseStage | null {
  if (!stage) {
    return "orchestrating";
  }

  const normalized = stage.toLowerCase() as TaskPulseStage;
  if (SUPPORTED_PULSE_STAGES.includes(normalized)) {
    return normalized;
  }

  return "orchestrating";
}

function normalizePulseKind(kind: string | null | undefined): TaskPulseEntryKind | undefined {
  if (!kind) {
    return undefined;
  }

  const normalized = kind.toLowerCase() as TaskPulseEntryKind;
  return SUPPORTED_PULSE_KINDS.includes(normalized) ? normalized : undefined;
}

function normalizeWorkspaceTargets(
  targets: TaskThreadPulseEntryWorkspaceTargetDto[] | null | undefined,
): TaskPulseEntryWorkspaceTarget[] | undefined {
  if (!targets?.length) {
    return undefined;
  }

  const normalized: TaskPulseEntryWorkspaceTarget[] = [];

  for (const target of targets) {
    if (!target || !target.namespaceId) {
      continue;
    }

    const kind = target.kind?.toLowerCase();
    if (kind !== "documents" && kind !== "files") {
      continue;
    }

    const hasPath = typeof target.path === "string" && target.path.length > 0;
    const hasReviewRange =
      typeof target.baseBranch === "string" &&
      target.baseBranch.length > 0 &&
      typeof target.headBranch === "string" &&
      target.headBranch.length > 0;

    // Keep back-compat: targets with a path are always valid. Targets without
    // a path are valid only when they carry a review-mode branch range.
    if (!hasPath && !hasReviewRange) {
      continue;
    }

    normalized.push({
      kind,
      namespaceId: target.namespaceId,
      repositoryId: target.repositoryId ?? undefined,
      repositoryName: target.repositoryName ?? undefined,
      branch: target.branch ?? undefined,
      baseBranch: target.baseBranch ?? undefined,
      headBranch: target.headBranch ?? undefined,
      path: hasPath ? target.path! : undefined,
    });
  }

  return normalized.length ? normalized : undefined;
}

function normalizeAuthorRole(role: unknown): UserCommentEvent["authorRole"] {
  const normalized =
    typeof role === "string" ? role : role == null ? "" : String(role);

  switch (normalized.trim().toLowerCase()) {
    case "operator":
      return "operator";
    case "stakeholder":
      return "stakeholder";
    case "external":
      return "external";
    default:
      return "system";
  }
}

function deriveInitials(name: string) {
  if (!name) {
    return "MA";
  }

  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "MA";
  }

  const first = parts[0][0]?.toUpperCase() ?? "";
  const second = parts.length > 1 ? parts[parts.length - 1][0]?.toUpperCase() ?? "" : "";
  const initials = `${first}${second}`.trim();
  return initials || first || "MA";
}

export function formatThreadTimestamp(timestamp: string | null | undefined) {
  if (!timestamp) {
    return "--:--";
  }

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return timestamp;
  }

  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
}

export function formatTimestamp(unixMs: number) {
  if (!Number.isFinite(unixMs) || unixMs <= 0) {
    return "--:--";
  }

  const date = new Date(unixMs);
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
}

function toUnixMs(timestamp: string | null | undefined): number {
  if (!timestamp) {
    return 0;
  }

  const parsed = Date.parse(timestamp);
  return Number.isFinite(parsed) ? parsed : 0;
}

function resolveTaskIdentifier(...candidates: Array<string | null | undefined>) {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed) {
      return trimmed;
    }
  }

  return "task";
}